import { api } from './client';
import type { UserRole } from '@/types/auth';

type Envelope<T> = { data: T };

export type TeamMemberStatus = 'active' | 'invited' | 'suspended' | 'deleted';

export type TeamMember = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: TeamMemberStatus;
  department: string | null;
  jobTitle: string | null;
  isEmailVerified: boolean;
  twoFaEnabled: boolean;
  lastLoginAt: string | null;
  invitedAt: string | null;
  createdAt: string;
};

export const usersApi = {
  async list(): Promise<TeamMember[]> {
    const { data } = await api.get<Envelope<TeamMember[]>>('/users');
    return data.data;
  },

  async invite(input: {
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    department?: string;
    jobTitle?: string;
  }): Promise<TeamMember> {
    const { data } = await api.post<Envelope<TeamMember>>('/users/invite', input);
    return data.data;
  },

  async resendInvite(uuid: string): Promise<{ sent: boolean }> {
    const { data } = await api.post<Envelope<{ sent: boolean }>>(`/users/${uuid}/resend-invite`);
    return data.data;
  },

  async update(uuid: string, input: Partial<{
    firstName: string;
    lastName: string;
    phone: string;
    department: string;
    jobTitle: string;
    language: string;
    timezone: string;
  }>): Promise<TeamMember> {
    const { data } = await api.patch<Envelope<TeamMember>>(`/users/${uuid}`, input);
    return data.data;
  },

  async updateMySignature(input: {
    signatureImageUrl?: string | null;
    initialImageUrl?: string | null;
  }): Promise<{ signatureImageUrl: string | null; initialImageUrl: string | null }> {
    const { data } = await api.patch<Envelope<{
      signatureImageUrl: string | null;
      initialImageUrl: string | null;
    }>>('/users/me/signature', input);
    return data.data;
  },

  async getNotificationPrefs(): Promise<Record<string, boolean>> {
    const { data } = await api.get<Envelope<Record<string, boolean>>>('/users/me/notifications');
    return data.data;
  },

  async updateNotificationPrefs(prefs: Record<string, boolean>): Promise<Record<string, boolean>> {
    const { data } = await api.patch<Envelope<Record<string, boolean>>>('/users/me/notifications', prefs);
    return data.data;
  },

  async changeRole(uuid: string, role: UserRole): Promise<TeamMember> {
    const { data } = await api.patch<Envelope<TeamMember>>(`/users/${uuid}/role`, { role });
    return data.data;
  },

  async remove(uuid: string): Promise<void> {
    await api.delete(`/users/${uuid}`);
  },

  async listPending(): Promise<TeamMember[]> {
    const { data } = await api.get<Envelope<TeamMember[]>>('/users/pending');
    return data.data;
  },

  async approve(uuid: string, role: 'admin' | 'manager' | 'member' | 'viewer'): Promise<TeamMember> {
    const { data } = await api.post<Envelope<TeamMember>>(`/users/${uuid}/approve`, { role });
    return data.data;
  },

  async reject(uuid: string): Promise<void> {
    await api.post(`/users/${uuid}/reject`);
  },

  async acceptInvite(token: string, password: string): Promise<void> {
    await api.post('/users/accept-invite', { token, password });
  },
};
